import React from 'react';
import { BADGE_STAT_KEYS, BADGE_STAT_LABELS, type BadgeStatKey } from 'features/catalog/badge-definitions';
import { deckWashOpacity, gradientFromDeckStats } from 'features/catalog/badge-hash-gradient';
import { computeRarityCounts, RARITY_TIER_LABELS } from 'features/catalog/rarity';
import { deckStatTotalTone, trioStatTotalTone } from 'features/catalog/rarity-config';
import { Badge } from 'features/catalog/useBadges';
import { BATTLE_DECK_SIZE, PILE_MAX_RARITY } from 'features/battle/types';
import { HoverTip } from 'shared/components/HoverTip';

export const sumDeckStats = (badges: readonly Badge[]): Record<BadgeStatKey, number> => {
  const totals: Record<BadgeStatKey, number> = {
    culture: 0,
    engineering: 0,
    growth: 0,
    fun: 0,
    spotlight: 0,
  };
  for (const badge of badges) {
    for (const key of BADGE_STAT_KEYS) totals[key] += badge.stats[key];
  }
  return totals;
};

/** Group fill that fades in a smooth wash mixed from the deck's stats. */
export const DeckGroupFill: React.FC<{
  totals: Record<BadgeStatKey, number>;
  count: number;
  opacity?: number;
}> = ({ totals, count, opacity: opacityOverride }) => {
  const wash = gradientFromDeckStats(totals) ?? undefined;
  const opacity = opacityOverride ?? deckWashOpacity(count, BATTLE_DECK_SIZE);
  return (
    <div className="gallery-group-fill" aria-hidden>
      <div className="gallery-group-fill-inner bb-deck-fill-inner" style={{ background: wash, opacity }} />
    </div>
  );
};

export const DeckGroupSwatch: React.FC<{
  totals: Record<BadgeStatKey, number>;
  count: number;
}> = ({ totals, count }) => {
  const wash = count > 0 ? gradientFromDeckStats(totals) : null;
  return (
    <span
      className={`gallery-group-swatch${wash ? '' : ' gallery-group-swatch--muted'}`}
      style={wash ? { background: wash } : undefined}
      aria-hidden
    />
  );
};

/** Remaining-deck pips above Draw. One pip per badge, colored like the pile dots. */
export const DeckRarityPips: React.FC<{
  badgeIds: readonly string[];
  badgesMap: Map<string, Badge>;
}> = ({ badgeIds, badgesMap }) => {
  const counts = computeRarityCounts(badgeIds, badgesMap);
  const summary = PILE_MAX_RARITY.filter((rarity) => counts[rarity] > 0)
    .map((rarity) => `${counts[rarity]} ${RARITY_TIER_LABELS[rarity]}`)
    .join(', ');
  if (!summary) return null;
  return (
    <HoverTip text={summary} className="is-stretch">
      <span className="bb-pile-pips bb-deck-rarity-pips" aria-label={summary}>
        {PILE_MAX_RARITY.map((rarity) =>
          counts[rarity] > 0 ? (
            <span key={rarity} className="bb-deck-rarity-group">
              {Array.from({ length: counts[rarity] }, (_, index) => (
                <span key={index} className={`bb-pile-pip rarity-${rarity} is-on`} />
              ))}
            </span>
          ) : null
        )}
      </span>
    </HoverTip>
  );
};

/** One-row stat totals, shown as a second header row on deck cards. */
export const DeckStats: React.FC<{
  totals: Record<BadgeStatKey, number>;
  preview?: Record<BadgeStatKey, number> | null;
  borderless?: boolean;
  trio?: boolean;
}> = ({ totals, preview, borderless, trio }) => {
  const shown = preview ?? totals;
  const peak = Math.max(1, ...BADGE_STAT_KEYS.map((key) => Math.max(totals[key], shown[key])));
  return (
    <aside
      className={`bb-pile bb-stat-pile bb-deck-stats bb-deck-stats--wide${borderless ? ' is-borderless' : ''}`}
      aria-label="Deck stats"
    >
      <span className="bb-pile-pips bb-stat-pile-label">Stats</span>
      <div className="bb-pile-stack bb-stat-bars">
        {BADGE_STAT_KEYS.map((key) => {
          const current = totals[key];
          const next = shown[key];
          const delta = next - current;
          const tone = trio ? trioStatTotalTone(next) : deckStatTotalTone(next, BATTLE_DECK_SIZE);
          // Totals this low barely count, so they read as gray like zero.
          const muted = next <= 2 && delta <= 0;
          const rarity = muted ? '' : ` rarity-${tone}`;
          const kept = Math.min(current, next);
          const shift = `${(kept / peak) * 100}%`;
          return (
            <div key={key} className={`bb-hover-stat${muted ? ' is-zero' : ''}${delta > 0 ? ' is-gain' : ''}`}>
              <span className={`bb-hover-stat-name${rarity}`}>{BADGE_STAT_LABELS[key]}</span>
              <span className="bb-hover-stat-track" aria-hidden>
                {current === 0 && next === 0 ? null : (
                  <span
                    className={`bb-hover-stat-fill${muted ? ' is-muted' : ` rarity-${tone}`}`}
                    style={{ width: shift }}
                  />
                )}
                {delta > 0 ? (
                  <span
                    className="bb-hover-stat-delta is-gain"
                    style={{ left: shift, width: `${(delta / peak) * 100}%` }}
                  />
                ) : null}
              </span>
              <b className={`bb-hover-stat-num stat-score-num${rarity}`}>{next}</b>
            </div>
          );
        })}
      </div>
    </aside>
  );
};
